/**
 * SendRoleSelector — choose which permission roles may SEND a template (CB-10.3, D5b, FR-21).
 *
 * Reuses the CB-8.1 permission-role enumeration idiom (ContractAccessSettings): the `roles` table,
 * ordered by name, showing global roles + this tenant's own roles (never another tenant's). The
 * selection is a set of `roles.id` (numeric — matches `tenant_settings.contract_access_role_ids`).
 * Empty selection = UNRESTRICTED (any user who can use Contracts may send). This control is purely
 * about *who may send*; it is distinct from recipient roles (who signs) — RecipientRoleMapper.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useTenant } from '@/contexts/TenantContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface SelectableRole {
  id: number;
  name: string;
  global: boolean | null;
  tenant_id: string | null;
}

interface SendRoleSelectorProps {
  /** Currently-selected `roles.id`s allowed to send. Empty = unrestricted. */
  value: number[];
  onChange: (roleIds: number[]) => void;
}

export function SendRoleSelector({ value, onChange }: SendRoleSelectorProps) {
  const { currentTenant } = useTenant();
  const [roles, setRoles] = useState<SelectableRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('roles')
          .select('id, name, global, tenant_id')
          .order('name');
        if (error) throw error;
        const tenantId = currentTenant?.tenant_id ?? null;
        const visible = (data ?? []).filter(
          (r) => r.global === true || (tenantId != null && r.tenant_id === tenantId),
        );
        if (!cancelled) setRoles(visible);
      } catch (err) {
        console.error('[SendRoleSelector] Failed to load roles:', err);
        if (!cancelled) setRoles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentTenant?.tenant_id]);

  const toggle = (roleId: number, checked: boolean) => {
    onChange(checked ? [...new Set([...value, roleId])] : value.filter((id) => id !== roleId));
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Who can send this template</Label>
      <p className="text-xs text-muted-foreground">
        {value.length === 0
          ? 'Anyone with Contracts access can send this template. Select roles to restrict it.'
          : 'Only the selected roles can send this template (superadmins always can).'}
      </p>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading roles&hellip;</p>
      ) : roles.length === 0 ? (
        <p className="text-sm text-muted-foreground">No permission roles found for this tenant.</p>
      ) : (
        <ul className="space-y-1.5">
          {roles.map((role) => (
            <li key={role.id}>
              <Label
                htmlFor={`send-role-${role.id}`}
                className="flex cursor-pointer items-center gap-2 text-sm font-normal"
              >
                <Checkbox
                  id={`send-role-${role.id}`}
                  checked={value.includes(role.id)}
                  onCheckedChange={(v) => toggle(role.id, v === true)}
                />
                {role.name}
              </Label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
